import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * MobileScrollbar
 *
 * Mobile browsers (especially iOS Safari) use auto-hiding "overlay" scrollbars
 * that fade out when you stop scrolling, and they ignore ::-webkit-scrollbar on
 * the page scroller. CSS alone therefore cannot keep a scrollbar visible there.
 *
 * This renders a lightweight, always-visible scrollbar for touch devices that
 * tracks window scroll position and can also be dragged to scroll.
 *
 * Performance notes:
 *  - scroll/resize listeners are passive and throttled with requestAnimationFrame
 *  - the thumb is moved with a transform (compositor-friendly, no layout thrash)
 *  - it only mounts on coarse-pointer (touch) devices, so desktop keeps the
 *    native scrollbar and pays no cost
 */
const TOUCH_QUERY = "(pointer: coarse)";
const MIN_THUMB_HEIGHT = 40;

const MobileScrollbar = () => {
  const [isTouch, setIsTouch] = useState(false);
  const [visible, setVisible] = useState(false);

  const thumbRef = useRef(null);
  const trackRef = useRef(null);
  const frameRef = useRef(null);
  const dragRef = useRef(null);

  // Only enable on touch devices.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mql = window.matchMedia(TOUCH_QUERY);
    const apply = () => setIsTouch(mql.matches);
    apply();

    if (mql.addEventListener) {
      mql.addEventListener("change", apply);
      return () => mql.removeEventListener("change", apply);
    }
    // Safari < 14
    mql.addListener(apply);
    return () => mql.removeListener(apply);
  }, []);

  const update = useCallback(() => {
    frameRef.current = null;

    const thumb = thumbRef.current;
    const track = trackRef.current;
    if (!thumb || !track) return;

    const doc = document.documentElement;
    const viewport = window.innerHeight;
    const scrollHeight = Math.max(
      doc.scrollHeight,
      document.body ? document.body.scrollHeight : 0
    );
    const scrollable = scrollHeight - viewport;

    // Nothing to scroll -> hide the bar entirely.
    if (scrollable <= 1) {
      setVisible(false);
      return;
    }
    setVisible(true);

    const trackHeight = track.clientHeight;
    const thumbHeight = Math.max(
      MIN_THUMB_HEIGHT,
      Math.round((viewport / scrollHeight) * trackHeight)
    );
    const maxThumbTop = trackHeight - thumbHeight;
    const scrollTop = window.scrollY || doc.scrollTop || 0;
    const ratio = Math.min(1, Math.max(0, scrollTop / scrollable));

    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translate3d(0, ${Math.round(
      ratio * maxThumbTop
    )}px, 0)`;
  }, []);

  const requestUpdate = useCallback(() => {
    if (frameRef.current != null) return;
    frameRef.current = window.requestAnimationFrame(update);
  }, [update]);

  useEffect(() => {
    if (!isTouch) return undefined;

    requestUpdate();

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });
    window.addEventListener("orientationchange", requestUpdate, {
      passive: true,
    });

    // Recalculate when page content grows/shrinks (lazy-loaded sections, etc).
    let observer;
    if (typeof ResizeObserver !== "undefined" && document.body) {
      observer = new ResizeObserver(requestUpdate);
      observer.observe(document.body);
    }

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      window.removeEventListener("orientationchange", requestUpdate);
      if (observer) observer.disconnect();
      if (frameRef.current != null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [isTouch, requestUpdate]);

  // Hide the native scrollbar while ours is active so there's never two bars.
  useEffect(() => {
    if (!isTouch) return undefined;
    const root = document.documentElement;
    root.classList.add("gf-has-mobile-scrollbar");
    return () => root.classList.remove("gf-has-mobile-scrollbar");
  }, [isTouch]);

  // ── Drag-to-scroll ────────────────────────────────────────────────────────
  const handlePointerDown = useCallback((e) => {
    const thumb = thumbRef.current;
    const track = trackRef.current;
    if (!thumb || !track) return;

    const doc = document.documentElement;
    const viewport = window.innerHeight;
    const scrollHeight = Math.max(
      doc.scrollHeight,
      document.body ? document.body.scrollHeight : 0
    );

    dragRef.current = {
      startY: e.clientY,
      startScroll: window.scrollY || doc.scrollTop || 0,
      scrollable: scrollHeight - viewport,
      maxThumbTop: track.clientHeight - thumb.offsetHeight,
    };

    if (thumb.setPointerCapture) {
      try {
        thumb.setPointerCapture(e.pointerId);
      } catch (_) {}
    }
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || drag.maxThumbTop <= 0) return;

    const delta = e.clientY - drag.startY;
    const next =
      drag.startScroll + (delta / drag.maxThumbTop) * drag.scrollable;

    window.scrollTo(0, Math.min(drag.scrollable, Math.max(0, next)));
    e.preventDefault();
  }, []);

  const endDrag = useCallback((e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    const thumb = thumbRef.current;
    if (thumb && thumb.releasePointerCapture && e?.pointerId != null) {
      try {
        thumb.releasePointerCapture(e.pointerId);
      } catch (_) {}
    }
  }, []);

  if (!isTouch) return null;

  return (
    <div
      ref={trackRef}
      className={`gf-mobile-scrollbar${visible ? " is-visible" : ""}`}
      aria-hidden="true"
    >
      <div
        ref={thumbRef}
        className="gf-mobile-scrollbar__thumb"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      />
    </div>
  );
};

export default MobileScrollbar;
